import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import logo from '../asset/logo.jpg';
import { motion } from 'framer-motion';
import { Calendar, User, Tag } from 'lucide-react';
import apiService from '../utils/api';

// Convert all <a> → green <button>
function transformLinksToButtons(html) {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const links = doc.querySelectorAll("a");

  links.forEach((a) => {
    const button = doc.createElement("button");

    button.textContent = a.textContent || "Open Link";
    button.setAttribute("data-href", a.href);

    // GREEN BUTTON STYLE
    button.setAttribute(
      "style",
      `
        background-color: #16a34a;
        color: white;
        padding: 10px 18px;
        border-radius: 10px;
        font-weight: 600;
        border: none;
        cursor: pointer;
        display: inline-block;
        margin: 10px 0;
      `
    );

    a.replaceWith(button);
  });

  return doc.body.innerHTML;
}

const NewsDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  // Fetch post + related posts
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await apiService.getBlogPost(id);
        setPost(response.data);

        const relatedResponse = await apiService.getBlogPosts({
          category: response.data.category,
          limit: 3,
        });

        setRelatedPosts(
          relatedResponse.data.posts.filter((p) => p.id !== response.data.id)
        );
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  // Make buttons clickable → open link
  useEffect(() => {
    if (!post) return;

    setTimeout(() => {
      const buttons = document.querySelectorAll("button[data-href]");

      const openLink = (e) => {
        const link = e.currentTarget.getAttribute("data-href");
        if (link) window.open(link, "_blank", "noopener,noreferrer");
      };

      buttons.forEach((btn) => btn.addEventListener("click", openLink));

      return () =>
        buttons.forEach((btn) =>
          btn.removeEventListener("click", openLink)
        );
    }, 80);
  }, [post]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Helmet>
  <title>{post?.title} — YS Sky Travel and Tour Agency P.L.C</title>
  <meta name="description" content={post?.summary || post?.title} />
  <meta property="og:image" content={post?.image} />
</Helmet>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Post not found
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
     <Helmet>
  <title>{post?.title} — Travel News</title>
  <meta name="description" content={post?.summary || post?.title} />
  <meta property="og:image" content={post?.image} />
</Helmet>

      <section className="relative h-96 bg-gradient-to-r from-primary-900 via-medical-600 to-blue-600 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={logo}
            alt="Header"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-medical-600/60"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Blog Details
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              explore the full story
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <article className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">

                {/* Meta */}
                <div className="flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 mb-6">
                  <div className="flex items-center mr-4">
                    <Calendar size={16} className="mr-2" />
                    {new Date(post.publish_date).toLocaleDateString()}
                  </div>
                  <div className="flex items-center mr-4">
                    <User size={16} className="mr-2" />
                    {post.author_name || "Admin"}
                  </div>
                  {post.category && (
                    <div className="flex items-center">
                      <Tag size={16} className="mr-2" />
                      {post.category}
                    </div>
                  )}
                </div>

                {/* Main Content WITH GREEN BUTTONS */}
                <div
                  className="prose dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: transformLinksToButtons(post.content),
                  }}
                />

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {JSON.parse(post.tags).map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-600 dark:text-gray-400"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            </motion.div>

            {/* Sidebar Related Posts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {relatedPosts.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
                  <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">
                    Related Posts
                  </h3>
                  <div className="space-y-6">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        to={`/news/${relatedPost.id}`}
                        className="block group"
                      >
                        <div className="mb-2 aspect-video rounded-lg overflow-hidden">
                          <img
                            src={`https://backend.ysskytravel.com${relatedPost.image_url}`}
                            alt={relatedPost.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-primary-500 transition-colors">
                          {relatedPost.title}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(
                            relatedPost.publish_date
                          ).toLocaleDateString()}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetail;
